// frontend/src/features/tasks/CompleteTaskModal.tsx
/**
 * Modal for completing task instances with optional data entry.
 * Shows instructions from the task template (read-only), type-specific fields,
 * notes, a completed-at date picker, and task completion history.
 */
import { useEffect } from 'react';
import {
  Modal,
  Drawer,
  TextInput,
  NumberInput,
  Textarea,
  Button,
  Group,
  Stack,
  Text,
  Paper,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconPencil, IconCalendarEvent } from '@tabler/icons-react';
import type { TaskInstance, CompleteTaskData } from '@/types/task';
import { ChemicalSelect } from '@/components/ChemicalSelect';
import { ParameterSelect } from '@/components/ParameterSelect';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTaskCompletionsByDefinition } from '@/hooks/useTasks';
import { TaskHistory } from './TaskHistory';

interface CompleteTaskModalProps {
  opened: boolean;
  onClose: () => void;
  instance: TaskInstance | null;
  onComplete: (data: CompleteTaskData) => void;
  onEditTask: () => void;
  isLoading?: boolean;
}

interface FormValues {
  notes: string;
  completed_at: Date;
  chemical: string;
  amount: number | undefined;
  brand: string;
  parameter: string;
  value: number | undefined;
  volume: number | undefined;
  percentage: number | undefined;
}

export function CompleteTaskModal({
  opened,
  onClose,
  instance,
  onComplete,
  onEditTask,
  isLoading,
}: CompleteTaskModalProps) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const volumeLabel = user?.volume_unit === 'gallons' ? 'gallons' : 'litres';

  // Fetch completion history for this task definition (only when modal is open)
  const { data: completionsData, isLoading: completionsLoading } = useTaskCompletionsByDefinition(
    opened ? instance?.definition_id : undefined
  );

  const form = useForm<FormValues>({
    initialValues: {
      notes: '',
      completed_at: new Date(),
      chemical: '',
      amount: undefined,
      brand: '',
      parameter: '',
      value: undefined,
      volume: undefined,
      percentage: undefined,
    },
  });

  // Reset form when instance changes
  useEffect(() => {
    if (instance) {
      form.setValues({
        notes: '',
        completed_at: new Date(),
        chemical: instance.template_data?.chemical || '',
        amount: instance.template_data?.amount ?? undefined,
        brand: instance.template_data?.brand || '',
        parameter: instance.template_data?.parameter || '',
        value: undefined,
        volume: instance.template_data?.volume || undefined,
        percentage: instance.template_data?.percentage || undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance?.id]);

  const handleSubmit = (values: FormValues) => {
    const data: CompleteTaskData = {
      notes: values.notes || undefined,
      completed_at: values.completed_at.toISOString(),
      chemical: values.chemical || undefined,
      amount: values.amount,
      brand: values.brand || undefined,
      parameter: values.parameter || undefined,
      value: values.value,
      volume: values.volume,
      percentage: values.percentage,
      equipment_id: instance?.equipment_id || undefined,
    };
    onComplete(data);
  };

  if (!instance) return null;

  const taskType = instance.task_type;
  const instructions = instance.template_data?.instructions;

  const title = <Text fw={600}>{`Complete: ${instance.name}`}</Text>;

  const content = (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        {/* Edit Task link */}
        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconPencil size={14} />}
            onClick={onEditTask}
          >
            Edit Task
          </Button>
        </Group>

        {/* Due date display */}
        <Paper p="sm" bg="gray.0" radius="sm" data-testid="due-date-display">
          <Group gap="xs">
            <IconCalendarEvent size={16} color="var(--mantine-color-dimmed)" />
            <Text size="sm" fw={500}>Due:</Text>
            <Text size="sm" c="dimmed">
              {new Date(instance.due_at).toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                ...(new Date(instance.due_at).getFullYear() !== new Date().getFullYear()
                  ? { year: 'numeric' }
                  : {}),
              })}
            </Text>
          </Group>
        </Paper>

        {/* Instructions (read-only) */}
        {instructions && (
          <Paper p="sm" bg="gray.0" radius="sm">
            <Text size="sm" fw={500} mb={4}>Instructions</Text>
            <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
              {instructions}
            </Text>
          </Paper>
        )}

        {/* Equipment (read-only) */}
        {instance.equipment_name && (
          <Paper p="sm" bg="gray.0" radius="sm">
            <Text size="sm" fw={500} mb={4}>Equipment</Text>
            <Text size="sm" c="dimmed">
              {instance.equipment_name}
            </Text>
          </Paper>
        )}

        {taskType === 'dosing' && (
          <>
            <ChemicalSelect
              value={form.values.chemical || ''}
              onChange={(val) => form.setFieldValue('chemical', val)}
              error={form.errors.chemical as string}
            />
            <NumberInput
              label="Amount (mL)"
              placeholder="Amount in mL"
              min={0}
              decimalScale={2}
              {...form.getInputProps('amount')}
            />
            <TextInput
              label="Brand"
              {...form.getInputProps('brand')}
            />
          </>
        )}

        {taskType === 'testing' && (
          <>
            <ParameterSelect
              value={form.values.parameter}
              onChange={(val) => form.setFieldValue('parameter', val)}
              error={form.errors.parameter as string}
            />
            <NumberInput
              label="Value"
              {...form.getInputProps('value')}
            />
          </>
        )}

        {taskType === 'water_change' && (
          <>
            <NumberInput
              label={`Volume (${volumeLabel})`}
              min={0}
              decimalScale={1}
              {...form.getInputProps('volume')}
            />
            <NumberInput
              label="Percentage"
              min={0}
              max={100}
              {...form.getInputProps('percentage')}
            />
          </>
        )}

        <Textarea
          label="Notes"
          placeholder="Optional notes about this completion"
          {...form.getInputProps('notes')}
        />

        <DateTimePicker
          label="Completed"
          maxDate={new Date()}
          value={form.values.completed_at}
          onChange={(val) => {
            if (val) {
              form.setFieldValue('completed_at', new Date(val));
            }
          }}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            Complete Task
          </Button>
        </Group>

        {/* Task completion history */}
        <TaskHistory
          completions={completionsData?.completions ?? []}
          isLoading={completionsLoading}
        />
      </Stack>
    </form>
  );

  return isMobile ? (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={title}
      position="bottom"
      size="85%"
      styles={{
        body: {
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        },
      }}
    >
      {content}
    </Drawer>
  ) : (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
    >
      {content}
    </Modal>
  );
}
